import markdownit from 'markdown-it';

const md = markdownit({ html: false, linkify: true });

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );
}

/**
 * Build a markdown transcript from a conversation. Filters out intermediate
 * assistant messages (mid-tool narration) and reduces `actions` entries to a
 * compact list of tool names. Used for the chat export feature in both
 * builder and analyst surfaces.
 */
function projectLine({ projectLabel, projectUrl, projectId }) {
  // Prefer a human label + link. Fall back to a link with "Open project" text
  // when only the URL is known. Last-ditch: the bare UUID. Render nothing if
  // we have no project info at all.
  const label = projectLabel || (projectUrl ? 'Open project' : projectId);
  if (!label) return null;
  if (projectUrl) return `**Project:** [${label}](${projectUrl})`;
  return `**Project:** ${label}`;
}

export function buildExportMarkdown({
  messages,
  projectId,
  projectLabel,
  projectUrl,
  title = 'Sesame AI conversation',
}) {
  const lines = [`# ${title}`, ''];
  const project = projectLine({ projectLabel, projectUrl, projectId });
  if (project) lines.push(project);
  lines.push(`**Exported:** ${new Date().toISOString()}`);
  lines.push('');
  lines.push('## Transcript', '');
  for (const msg of messages || []) {
    if (msg.role === 'user') {
      lines.push('### User', '', msg.content || '', '');
    } else if (msg.role === 'assistant' && !msg.intermediate) {
      lines.push('### Assistant', '', msg.content || '', '');
    } else if (msg.role === 'actions' && Array.isArray(msg.actions)) {
      const names = msg.actions.map(a => a.actionType).filter(Boolean);
      if (names.length > 0) {
        lines.push('### Actions', '', names.map(n => `- ${n}`).join('\n'), '');
      }
    } else if (msg.role === 'result' && msg.actionType) {
      lines.push(
        '### Result',
        '',
        `**${msg.actionType}**${msg.pathwayName ? ` — ${msg.pathwayName}` : ''}`,
        '',
      );
    } else if (msg.role === 'system' && msg.content) {
      lines.push('### Notice', '', `_${msg.content}_`, '');
    }
  }
  return lines.join('\n');
}

const PRINT_CSS = `
  @page { size: letter; margin: 0.5in; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #1f2937; line-height: 1.55; max-width: 720px; margin: 0 auto; padding: 1.5em 1.25em; font-size: 13px; }
  h1 { font-size: 20px; margin: 0 0 0.4em; border-bottom: 1px solid #cbd5e1; padding-bottom: 0.4em; }
  h2 { font-size: 15px; margin: 1.5em 0 0.3em; color: #334155; }
  h3 { font-size: 12px; margin: 1.2em 0 0.4em; color: #475569; text-transform: uppercase; letter-spacing: 0.04em; }
  p, ul, ol { margin: 0.5em 0; }
  ul, ol { padding-left: 1.4em; }
  table { border-collapse: collapse; margin: 0.75em 0; font-size: 12px; width: 100%; }
  th, td { border: 1px solid #cbd5e1; padding: 4px 8px; text-align: left; vertical-align: top; }
  th { background: #f1f5f9; }
  code { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 12px; }
  pre { background: #f1f5f9; padding: 0.5em; border-radius: 4px; overflow: auto; }
  hr { border: none; border-top: 1px solid #e2e8f0; margin: 1.5em 0; }
  a { color: #1d4ed8; }
  blockquote { border-left: 3px solid #cbd5e1; padding: 0 1em; color: #475569; margin: 0.75em 0; }
  .print-banner { position: sticky; top: 0; background: #1f2937; color: #fff; padding: 0.5em 1em; margin: -1.5em -1.25em 1em; font-size: 12px; display: flex; justify-content: space-between; align-items: center; gap: 1em; z-index: 10; }
  .print-banner button { background: #fff; color: #1f2937; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer; font-size: 12px; }
  .print-banner button:hover { background: #e2e8f0; }
  @media print { .print-banner { display: none; } body { padding: 0; max-width: none; } }
`;

export function buildExportHtml(opts) {
  const markdown = buildExportMarkdown(opts);
  const body = md.render(markdown);
  const title = opts?.title ?? 'Sesame AI conversation';
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>${PRINT_CSS}</style>
</head>
<body>
<div class="print-banner">
  <span>Cmd / Ctrl + P → "Save as PDF" to download</span>
  <button onclick="window.print()">Print / Save as PDF</button>
</div>
${body}
</body>
</html>`;
}

/**
 * Open a self-contained styled HTML view of the conversation in a new tab.
 * Tables, lists, and headings render properly. The user can use the browser's
 * Print → "Save as PDF" to export a real PDF. Falls back to an HTML download
 * if the popup is blocked.
 */
export function exportConversation(opts) {
  const html = buildExportHtml(opts);
  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    return;
  }
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  a.download = `sesame-conversation-${stamp}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
